import { Box, Drawer, Typography } from '@mui/material';
import Loading from 'components/common/Loading';
import SearchField from 'components/controls/SearchField';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLazyListVisibleQuery } from 'store/app/entities/EmployeesApiSlice';
import channelDrawer from 'store/app/ui/drawers/channel/action';
import directDrawer from 'store/app/ui/drawers/direct/action';
import isCompanyAdminSelector from 'store/selectors/isCompanyAdmin';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';
import useDebouncePromise from 'utils/useDebouncePromise';
import CloseButton from '../CloseButton';
import DirectsList from './DirectsList';
import NewChannelArea from './NewChannelArea';

const DirectDrawer = () => {
  const employeesRef = useRef({});
  const [search, setSearch] = useState('');
  const [handleClose, openCreateChannel] = useActions([directDrawer.close, channelDrawer.open]);
  const isOpen = useSelector(state => state.drawers.direct.isOpen);
  const [triggerListVisibleQuery] = useLazyListVisibleQuery();
  const [isLoading, setLoading] = useState(true);
  const [isFetching, setFetching] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const isCompanyAdmin = useSelector(isCompanyAdminSelector);

  const listVisibleQuery = useDebouncePromise(
    useCallback(
      (...args) => {
        setFetching(true);
        return triggerListVisibleQuery(...args)
          .unwrap()
          .then(result => {
            setTotalCount(result.metadata.total);
            result.employees?.forEach((e, i) => {
              employeesRef.current[result.metadata.skip + i] = e;
            });
            return result;
          })
          .finally(() => {
            setFetching(false);
            setLoading(false);
          });
      },
      [triggerListVisibleQuery],
    ),
    300,
  );

  useEffect(() => {
    listVisibleQuery({
      search,
      skip: 0,
      limit: 99,
    });
  }, [search, listVisibleQuery]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setTotalCount(0);
      setSearch('');
      listVisibleQuery({
        search: '',
        skip: 0,
        limit: 99,
      });
    }
  }, [isOpen, listVisibleQuery]);

  const handleSearch = useCallback(event => {
    setSearch(event.target.value);
  }, []);

  const loadMoreItems = useCallback(
    (startIndex, stopIndex) => {
      return listVisibleQuery({
        search,
        skip: startIndex,
        limit: stopIndex - startIndex + 1,
      });
    },
    [listVisibleQuery, search],
  );

  if (!isOpen) return null;

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <Box sx={{ display: 'flex', minWidth: 650, flexGrow: 1 }}>
        <Box sx={{ paddingTop: '20px', paddingLeft: '20px' }}>
          <CloseButton onClick={handleClose} />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            padding: '20px',
            paddingBottom: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h5" sx={{ paddingBottom: '25px' }}>
            {I18n.t('EmployeesDrawer.New Chat')}
          </Typography>
          <SearchField onChange={handleSearch} />
          {isCompanyAdmin && <NewChannelArea onClick={openCreateChannel} />}
          {!isLoading || <Loading />}
          {isLoading || !totalCount || (
            <DirectsList
              isFetching={isFetching}
              employeesRef={employeesRef}
              loadMoreItems={loadMoreItems}
              totalCount={totalCount}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default memo(DirectDrawer);
